/**
 * IP Geolocation utility for CloudTalk Network Diagnostics.
 *
 * Looks up geographical location data (city, country, ISP, lat/lon) for IP addresses
 * using free public APIs. Used to show client and server locations on a map in the
 * diagnostic report.
 *
 * API chain (all support HTTPS):
 *   1. ipwho.is  — free, HTTPS, no key, 10k req/month
 *   2. ipapi.co  — free, HTTPS, no key, 1k req/day
 *   3. ip-api.com — free, HTTP only (may be blocked by mixed-content policy)
 */

export interface GeoLocationInfo {
  ip: string;
  ipVersion: 'IPv4' | 'IPv6' | 'unknown';
  city: string | null;
  region: string | null;
  country: string | null;
  countryCode: string | null;
  lat: number | null;
  lon: number | null;
  isp: string | null;
  org: string | null;
  as: string | null;
  timezone: string | null;
}

export interface LocationInfo {
  client: GeoLocationInfo | null;
  server: GeoLocationInfo | null;
  distance: number | null; // km between client and server (Haversine)
  twilioEdge: string | null; // Inferred Twilio edge name from server city (e.g. "ashburn", "frankfurt")
  lookupTimestamp: string;
  lookupErrors: string[];
}

/**
 * Detect IP version from string format.
 */
export function detectIPVersion(ip: string): 'IPv4' | 'IPv6' | 'unknown' {
  if (!ip) return 'unknown';
  if (ip.includes(':')) return 'IPv6';
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip)) return 'IPv4';
  return 'unknown';
}

/**
 * Calculate distance between two lat/lon points using the Haversine formula.
 * @returns Distance in kilometers, rounded to nearest integer.
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

/** Helper: fetch with timeout */
async function fetchWithTimeout(url: string, timeoutMs: number = 5000): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

/**
 * API 1: ipwho.is — HTTPS, free, no key, 10k req/month.
 */
async function lookupWithIpWhois(ip: string): Promise<GeoLocationInfo> {
  const response = await fetchWithTimeout(`https://ipwho.is/${encodeURIComponent(ip)}`);
  const data = await response.json();

  if (data.success === false) {
    throw new Error(data.message || 'ipwho.is lookup failed');
  }

  return {
    ip: data.ip || ip,
    ipVersion: detectIPVersion(data.ip || ip),
    city: data.city || null,
    region: data.region || null,
    country: data.country || null,
    countryCode: data.country_code || null,
    lat: data.latitude ?? null,
    lon: data.longitude ?? null,
    isp: data.connection?.isp || null,
    org: data.connection?.org || null,
    as: data.connection?.asn ? `AS${data.connection.asn}` : null,
    timezone: data.timezone?.id || null,
  };
}

/**
 * API 2: ipapi.co — HTTPS, free tier 1000 req/day, no key needed.
 */
async function lookupWithIpapiCo(ip: string): Promise<GeoLocationInfo> {
  const response = await fetchWithTimeout(`https://ipapi.co/${encodeURIComponent(ip)}/json/`);
  const data = await response.json();

  if (data.error) {
    throw new Error(data.reason || 'ipapi.co lookup failed');
  }

  return {
    ip: data.ip || ip,
    ipVersion: detectIPVersion(data.ip || ip),
    city: data.city || null,
    region: data.region || null,
    country: data.country_name || null,
    countryCode: data.country_code || null,
    lat: data.latitude ?? null,
    lon: data.longitude ?? null,
    isp: data.org || null,
    org: data.org || null,
    as: data.asn ? `AS${data.asn}` : null,
    timezone: data.timezone || null,
  };
}

/**
 * API 3: ip-api.com — HTTP only (may be blocked by mixed-content), 45 req/min.
 */
async function lookupWithIpApi(ip: string): Promise<GeoLocationInfo> {
  const response = await fetchWithTimeout(
    `http://ip-api.com/json/${encodeURIComponent(ip)}?fields=status,message,country,countryCode,regionName,city,lat,lon,timezone,isp,org,as,query`
  );
  const data = await response.json();

  if (data.status === 'fail') {
    throw new Error(data.message || 'ip-api.com lookup failed');
  }

  return {
    ip: data.query || ip,
    ipVersion: detectIPVersion(data.query || ip),
    city: data.city || null,
    region: data.regionName || null,
    country: data.country || null,
    countryCode: data.countryCode || null,
    lat: data.lat ?? null,
    lon: data.lon ?? null,
    isp: data.isp || null,
    org: data.org || null,
    as: data.as || null,
    timezone: data.timezone || null,
  };
}

/**
 * Look up geolocation for an IP address.
 * Tries APIs in order: ipwho.is → ipapi.co → ip-api.com → partial result.
 */
export async function lookupIPGeolocation(ip: string): Promise<GeoLocationInfo> {
  // Skip lookup for private/reserved IPs — they won't geolocate
  if (isPrivateIP(ip)) {
    return makePartialResult(ip);
  }

  const apis = [
    { name: 'ipwho.is', fn: lookupWithIpWhois },
    { name: 'ipapi.co', fn: lookupWithIpapiCo },
    { name: 'ip-api.com', fn: lookupWithIpApi },
  ];

  for (const api of apis) {
    try {
      return await api.fn(ip);
    } catch (error) {
      console.warn(`IP geo API ${api.name} failed:`, error);
    }
  }

  // All APIs failed — return partial result with just the IP
  return makePartialResult(ip);
}

/**
 * Create a partial GeoLocationInfo with just the IP and null geo fields.
 */
function makePartialResult(ip: string): GeoLocationInfo {
  return {
    ip,
    ipVersion: detectIPVersion(ip),
    city: null,
    region: null,
    country: null,
    countryCode: null,
    lat: null,
    lon: null,
    isp: null,
    org: null,
    as: null,
    timezone: null,
  };
}

/**
 * Check if an IP is private/reserved (won't geolocate).
 */
function isPrivateIP(ip: string): boolean {
  // IPv4 private ranges
  if (
    ip.startsWith('10.') ||
    ip.startsWith('192.168.') ||
    ip.startsWith('127.') ||
    ip.startsWith('0.')
  ) {
    return true;
  }
  // 172.16.0.0 - 172.31.255.255
  if (ip.startsWith('172.')) {
    const second = parseInt(ip.split('.')[1], 10);
    if (second >= 16 && second <= 31) return true;
  }
  // IPv6 loopback and link-local
  if (ip === '::1' || ip.startsWith('fe80:') || ip.startsWith('fc') || ip.startsWith('fd')) {
    return true;
  }
  return false;
}

/**
 * Orchestrate geolocation lookups for both client and server IPs.
 * Runs both lookups in parallel for speed.
 */
export async function lookupBothLocations(
  clientIP: string,
  serverIP: string | null
): Promise<LocationInfo> {
  const errors: string[] = [];
  let client: GeoLocationInfo | null = null;
  let server: GeoLocationInfo | null = null;

  const [clientResult, serverResult] = await Promise.allSettled([
    lookupIPGeolocation(clientIP),
    serverIP ? lookupIPGeolocation(serverIP) : Promise.resolve(null),
  ]);

  if (clientResult.status === 'fulfilled') {
    client = clientResult.value;
  } else {
    errors.push(`Client IP lookup failed: ${clientResult.reason}`);
  }

  if (serverResult.status === 'fulfilled') {
    server = serverResult.value;
  } else {
    errors.push(`Server IP lookup failed: ${serverResult.reason}`);
  }

  // Calculate distance if both have coordinates
  let distance: number | null = null;
  if (
    client?.lat != null &&
    client?.lon != null &&
    server?.lat != null &&
    server?.lon != null
  ) {
    distance = calculateDistance(client.lat, client.lon, server.lat, server.lon);
  }

  return {
    client,
    server,
    distance,
    twilioEdge: inferTwilioEdge(server),
    lookupTimestamp: new Date().toISOString(),
    lookupErrors: errors,
  };
}

/**
 * Infer Twilio edge name from the geolocated server city.
 * Twilio uses AWS/GCP data centers in known locations.
 * Returns null if the city doesn't match any known edge.
 */
export function inferTwilioEdge(serverGeo: GeoLocationInfo | null): string | null {
  if (!serverGeo?.city) return null;
  const city = serverGeo.city.toLowerCase();

  // Twilio's known edge locations (city substring → edge name)
  const edgeMap: [string, string][] = [
    ['ashburn', 'ashburn'],
    ['dublin', 'dublin'],
    ['frankfurt', 'frankfurt'],
    ['singapore', 'singapore'],
    ['sydney', 'sydney'],
    ['tokyo', 'tokyo'],
    ['são paulo', 'sao-paulo'],
    ['sao paulo', 'sao-paulo'],
    ['hong kong', 'hong-kong'],
    ['mumbai', 'mumbai'],
    // AWS data center locations that Twilio uses (common geo API results)
    ['boardman', 'us-west-2'],       // Oregon — AWS us-west-2
    ['san jose', 'us-west-1'],       // California — AWS us-west-1
    ['columbus', 'us-east-2'],       // Ohio — AWS us-east-2
    ['montreal', 'ca-central-1'],    // Canada — AWS ca-central-1
    ['london', 'london'],
    ['mumbai', 'mumbai'],
    ['seoul', 'seoul'],
    ['osaka', 'osaka'],
  ];

  for (const [key, edge] of edgeMap) {
    if (city.includes(key)) return edge;
  }
  return null;
}
